// Çalışma zamanı importlarında uzantı ŞART: `node --experimental-strip-types`
// tipleri söküp dosyayı olduğu gibi çalıştırıyor, uzantısız yol çözülmüyor.
// Testler bu yolla koşuyor; tsc uzantısız hâlden şikayet etmediği için hata
// ancak testi çalıştırınca çıkıyor.
import {
  MAX_ADDED, PATTERN_LABEL, PRESCRIPTION, candidatesFor, estimatedMinutes, muscleTr,
} from './program.ts'
import type { CatalogExercise, Level, Pattern, ProgramEdits, WorkoutDay } from './program'
import { VOLUME, contribution } from './training.ts'

/**
 * "Göğsün eksik" deyip bırakmak yerine ne yapılacağını söylemek.
 *
 * Haftalık yük tablosu bir kasın hedefin altında olduğunu zaten gösteriyordu
 * ama oradan sonrası kullanıcıya kalıyordu: hangi hareket, hangi güne, kaç set
 * kazandırır, seans ne kadar uzar. Bu soruların cevabı verinin içinde duruyor;
 * kullanıcının kafasında hesaplamasını beklemek gereksiz.
 *
 * Öneri OTOMATİK UYGULANMIYOR. Program kendi kendine hareket eklemeye başlarsa
 * seans süresi sessizce büyür ve kimse neden uzadığını bilmez; üstelik kullanıcı
 * da elle eklerken hacim iki yerden birden artar. Burada üretilen şey bir teklif:
 * sayısı, süresi ve yan etkisi yazılı, uygulayan kullanıcı.
 */

export interface HacimAcigi {
  muscle: string
  /** Programın vaat ettiği haftalık fraksiyonel set. */
  planned: number
  target: number
  eksik: number
}

export interface EkKatki {
  muscle: string
  sets: number
}

export interface Oneri {
  /** Açığı kapatmak için önerilen hareket. */
  exercise: CatalogExercise
  /** Hangi kasın açığı için önerildi. */
  muscle: string
  dayLabel: string
  dayName: string
  pattern: Pattern
  sets: number
  /** Hedef kasa haftalık fraksiyonel kazanç. */
  kazanc: number
  /** Eklendikten sonra hedef kasın haftalık planı. */
  sonrasi: number
  /** Aynı hareketin diğer kaslara katkısı — yan etki gizlenmiyor. */
  yanKatki: EkKatki[]
  /** Seans süresine etkisi (dakika). */
  ekDakika: number
  /** Birincil kas mı (1.0) yoksa ikincil mi (0.5). */
  birincil: boolean
}

/**
 * Elle eklenen hareketin reçetesi. program.ts'teki kuralın AYNISI olmak
 * zorunda, o yüzden tabloyu kopyalamıyoruz — oradan import ediyoruz.
 * Kopyalanan sürüm bir kez saptı ve kullanıcıya yanlış sayı gösterdi.
 */
function reçete(pattern: Pattern) {
  return PRESCRIPTION[pattern] ?? PRESCRIPTION.accessory
}

/** Bir hareketin o güne eklenmesinin süre maliyeti. */
function dakikaMaliyeti(pattern: Pattern): number {
  const r = reçete(pattern)
  const work = r.unit === 'sec' ? r.high : r.high * 3
  return Math.round((r.sets * (work + r.rest) * 1.15) / 60)
}

/**
 * Hedefin altında kalan kaslar. `target` faz hedefi (genelde 10 set/kas).
 *
 * Eşik olarak MEV değil TARGET kullanılıyor: "büyüme başlıyor" ile "hedefteyim"
 * farklı şeyler ve kullanıcı ikincisini istiyor. Ama 0.5 setlik açıklar
 * elenmiyorsa tablo sürekli öneri gösterir — anlamlı fark aranıyor.
 */
export function hacimAciklari(
  planned: Record<string, number>, target: number, esik = 1.5,
): HacimAcigi[] {
  const out: HacimAcigi[] = []
  for (const [muscle, p] of Object.entries(planned)) {
    if (muscle === 'unknown') continue
    const eksik = target - p
    if (eksik >= esik) out.push({ muscle, planned: p, target, eksik })
  }
  // En büyük açık üstte: kullanıcı listeyi baştan okuyup bırakıyor.
  return out.sort((a, b) => b.eksik - a.eksik)
}

/**
 * Bir kasın açığını kapatacak hareket önerileri.
 *
 * Kurallar:
 *  - Programda zaten olan hareket önerilmiyor (aynı şeyi iki kez yapmak hacim değil).
 *  - Ekipmanı olmayan hareket önerilmiyor.
 *  - Gün başına elle ekleme sınırı (MAX_ADDED) dolmuş güne önerilmiyor.
 *  - Tavanı (CAP) aşacak öneri elenıyor: fazla hacim fayda değil, toparlanma borcu.
 *  - Seans başına kas tavanı (PER_SESSION_CAP) aşılıyorsa o gün elenıyor —
 *    aynı seansta bir kasa 8 setten fazlası boşa gidiyor.
 */
export function onerilerFor(o: {
  muscle: string
  catalog: CatalogExercise[]
  days: WorkoutDay[]
  edits: ProgramEdits
  equipment: string[]
  strengthPerWeek: number
  planned: Record<string, number>
  level?: Level
  limit?: number
}): Oneri[] {
  const { muscle, catalog, days, edits, equipment, planned } = o
  if (days.length === 0) return []

  const perWeek = o.strengthPerWeek > 0 ? o.strengthPerWeek : 3
  const timesPerWeek = perWeek / days.length
  const mevcut = new Set(days.flatMap((d) => d.exercises.map((e) => e.exercise.slug)))
  const suAn = planned[muscle] ?? 0

  const oneriler: Oneri[] = []
  const PATTERNS: Pattern[] =
    ['squat', 'hinge', 'push_h', 'push_v', 'pull_h', 'pull_v', 'core', 'accessory']

  for (const pattern of PATTERNS) {
    for (const ex of candidatesFor(catalog, pattern, equipment, o.level ?? 'beginner')) {
      if (mevcut.has(ex.slug)) continue
      const katki = contribution(ex)
      const w = katki[muscle]
      if (!w) continue

      for (const day of days) {
        // Elle kurulmuş günün içeriğine karışmıyoruz: kullanıcı orada
        // üreticiyi tamamen devre dışı bırakmış, öneriyle geri sızmak yanlış.
        if (day.custom) continue
        if ((edits[day.label]?.add ?? []).length >= MAX_ADDED) continue

        const r = reçete(pattern)
        const kazanc = r.sets * w * timesPerWeek
        if (kazanc <= 0) continue
        if (suAn + kazanc > VOLUME.CAP) continue

        // Seans başına kas tavanı — bu gün bu kasa zaten ne kadar yüklüyor.
        const gunIci = day.exercises.reduce(
          (t, e) => t + e.sets * (contribution(e.exercise)[muscle] ?? 0), 0)
        if (gunIci + r.sets * w > VOLUME.PER_SESSION_CAP) continue

        oneriler.push({
          exercise: ex, muscle, dayLabel: day.label,
          dayName: day.name ?? `Antrenman ${day.label}`,
          pattern, sets: r.sets,
          kazanc: Math.round(kazanc * 10) / 10,
          sonrasi: Math.round((suAn + kazanc) * 10) / 10,
          yanKatki: Object.entries(katki)
            .filter(([m]) => m !== muscle)
            .map(([m, v]) => ({ muscle: m, sets: Math.round(r.sets * v * timesPerWeek * 10) / 10 }))
            .sort((a, b) => b.sets - a.sets),
          ekDakika: dakikaMaliyeti(pattern),
          birincil: w >= 1,
        })
      }
    }
  }

  // Sıralama: önce birincil kas (ikincil katkı "göğüs çalıştım" saymaz),
  // sonra kısa seansa ekleyen (süreyi dengeliyoruz), sonra büyük kazanç,
  // en son ad — aynı skorda liste her yenilemede zıplamasın.
  const sure = new Map(days.map((d) => [d.label, estimatedMinutes(d)]))
  oneriler.sort((a, b) =>
    Number(b.birincil) - Number(a.birincil) ||
    (sure.get(a.dayLabel) ?? 0) - (sure.get(b.dayLabel) ?? 0) ||
    b.kazanc - a.kazanc ||
    a.exercise.nameTr.localeCompare(b.exercise.nameTr, 'tr'))

  // Aynı hareketi iki güne birden önermiyoruz: kullanıcı hangisini seçeceğini
  // değil, ne yapacağını sormak istiyor.
  const gorulen = new Set<string>()
  const tekil = oneriler.filter((x) => !gorulen.has(x.exercise.slug) && gorulen.add(x.exercise.slug))
  return tekil.slice(0, o.limit ?? 3)
}

/** Öneri satırının tek cümlelik özeti. */
export function oneriMetni(x: Oneri): string {
  const yan = x.yanKatki.filter((y) => y.sets >= 0.5).slice(0, 2)
  const yanMetin = yan.length
    ? ` Ayrıca ${yan.map((y) => `${muscleTr(y.muscle)} +${n(y.sets)}`).join(', ')}.`
    : ''
  return `${x.dayName} gününe ekle: ${muscleTr(x.muscle)} ${n(x.kazanc)} set artar `
    + `(${n(x.sonrasi)} olur), seans ~${x.ekDakika} dk uzar.${yanMetin}`
}

const n = (v: number) => (Math.round(v * 10) / 10).toLocaleString('tr-TR')

/** Kalıp adı — öneri kartında "neyin yerine geçtiği" belli olsun diye. */
export function patternAdi(p: Pattern): string {
  return PATTERN_LABEL[p] ?? p
}
